/**
 * NEXUS UI - ProgressBar
 * Linear progress indicator with segments and energy flow animation
 */

import React from 'react';
import type { BaseProps, NexusVariant, NexusSize } from './types';

export interface ProgressBarProps extends BaseProps {
  value: number;
  max?: number;
  variant?: NexusVariant;
  size?: NexusSize;
  segments?: number;
  showLabels?: boolean;
  animated?: boolean;
  label?: string;
  showValue?: boolean;
}

const variantColors: Record<NexusVariant, { base: string; dim: string; glow: string }> = {
  cyan: { base: '#00d4ff', dim: '#0d4f5c', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', dim: '#1a3a5c', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', dim: '#3b1a5c', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', dim: '#0d4a2a', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', dim: '#5c3d0d', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', dim: '#5c1a1a', glow: 'rgba(255, 51, 102, 0.6)' },
};

const sizeConfig: Record<NexusSize, { height: number; borderRadius: number }> = {
  sm: { height: 4, borderRadius: 2 },
  md: { height: 8, borderRadius: 4 },
  lg: { height: 12, borderRadius: 6 },
  xl: { height: 16, borderRadius: 8 },
};

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  variant = 'cyan',
  size = 'md',
  segments = 0,
  showLabels = false,
  animated = true,
  label,
  showValue = false,
  className = '',
  style,
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const colors = variantColors[variant];
  const config = sizeConfig[size];

  return (
    <div
      className={`nx-progress-bar ${animated ? 'nx-progress-bar--animated' : ''} ${className}`}
      style={style}
    >
      {(label || showValue) && (
        <div className="nx-progress-bar__header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}>
          {label && (
            <span className="nx-progress-bar__title" style={{
              fontFamily: 'var(--nx-font-interface)',
              fontSize: 'var(--nx-text-xs)',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--nx-text-secondary)',
            }}>
              {label}
            </span>
          )}
          {showValue && (
            <span className="nx-progress-bar__percentage" style={{
              fontFamily: 'var(--nx-font-mono)',
              fontSize: 'var(--nx-text-sm)',
              fontWeight: 600,
              color: colors.base,
            }}>
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}

      <div
        className="nx-progress-bar__track"
        style={{
          height: config.height,
          borderRadius: config.borderRadius,
        }}
      >
        <div
          className="nx-progress-bar__fill"
          style={{
            width: `${percentage}%`,
            '--bar-color': colors.base,
            '--bar-dim': colors.dim,
            '--bar-glow': colors.glow,
            height: '100%',
            borderRadius: config.borderRadius,
            background: `linear-gradient(90deg, ${colors.dim}, ${colors.base})`,
            boxShadow: `0 0 12px ${colors.glow}`,
            transition: animated ? 'width 0.8s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
            position: 'relative',
            overflow: 'hidden',
          } as React.CSSProperties}
        >
          {/* Shimmer effect - only shows briefly on value change, not continuously */}
        </div>

        {/* Segments overlay */}
        {segments > 0 && (
          <div
            className="nx-progress-bar__segments"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              gap: '2px',
            }}
          >
            {Array.from({ length: segments }).map((_, i) => (
              <div
                key={i}
                className="nx-progress-bar__segment"
                style={{
                  flex: 1,
                  borderRight: i < segments - 1 ? '2px solid var(--nx-void-deep)' : 'none',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {showLabels && (
        <div className="nx-progress-bar__labels">
          <span className="nx-progress-bar__label">0</span>
          <span className="nx-progress-bar__label">{max}</span>
        </div>
      )}
    </div>
  );
};

export default ProgressBar;
